import React, { useState, useRef, useEffect, useCallback } from 'react';
import { ChevronLeft, ChevronRight, X, ZoomIn } from 'lucide-react';
import { galleryImages } from '../data/galleryData';
import './krina-gallery.css';

/**
 * Krina Gallery — full-width auto-scrolling image marquee.
 *
 * Behaviour mirrors ReviewsCarousel:
 *  - Right-to-left continuous scroll (linear marquee).
 *  - Pauses on hover, focus, prev/next button use, or touch interaction.
 *  - Resumes ~4s after visitor stops interacting.
 *
 * Click a tile -> minimal dark-backdrop lightbox with prev/next arrows,
 * Esc/click-backdrop to close, keyboard ← → navigation, swipe on mobile,
 * image counter, and fade transitions.
 */

const SECONDS_PER_TILE = 4;
const RESUME_AFTER_IDLE_MS = 4000;

const KrinaGallery = () => {
  const [isPaused, setIsPaused] = useState(false);
  const [lightboxIdx, setLightboxIdx] = useState(null);
  const idleTimerRef = useRef(null);
  const trackRef = useRef(null);
  const touchStartX = useRef(null);

  const totalDurationSec = galleryImages.length * SECONDS_PER_TILE;

  const pauseAndResume = useCallback(() => {
    setIsPaused(true);
    if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
    idleTimerRef.current = setTimeout(() => setIsPaused(false), RESUME_AFTER_IDLE_MS);
  }, []);

  useEffect(() => () => {
    if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
  }, []);

  const step = useCallback((direction) => {
    pauseAndResume();
    const node = trackRef.current;
    if (!node) return;
    const firstCard = node.querySelector('.krina-gallery-tile');
    if (!firstCard) return;
    const tileWidth = firstCard.getBoundingClientRect().width
      + parseFloat(getComputedStyle(node).gap || '0');
    node.scrollBy({ left: direction * tileWidth, behavior: 'smooth' });
  }, [pauseAndResume]);

  const openLightbox = useCallback((idx) => {
    setLightboxIdx(idx);
    setIsPaused(true);
  }, []);

  const closeLightbox = useCallback(() => {
    setLightboxIdx(null);
    if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
    idleTimerRef.current = setTimeout(() => setIsPaused(false), 200);
  }, []);

  const goNext = useCallback(() => {
    setLightboxIdx((i) => (i === null ? null : (i + 1) % galleryImages.length));
  }, []);

  const goPrev = useCallback(() => {
    setLightboxIdx((i) => (i === null ? null : (i - 1 + galleryImages.length) % galleryImages.length));
  }, []);

  // Keyboard support + body scroll lock while lightbox open
  useEffect(() => {
    if (lightboxIdx === null) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') closeLightbox();
      else if (e.key === 'ArrowRight') goNext();
      else if (e.key === 'ArrowLeft') goPrev();
    };
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [lightboxIdx, closeLightbox, goNext, goPrev]);

  const onTouchStart = (e) => { touchStartX.current = e.touches[0].clientX; };
  const onTouchEnd = (e) => {
    if (touchStartX.current === null) return;
    const dx = e.changedTouches[0].clientX - touchStartX.current;
    if (Math.abs(dx) > 50) {
      if (dx < 0) goNext();
      else goPrev();
    }
    touchStartX.current = null;
  };

  const currentImage = lightboxIdx !== null ? galleryImages[lightboxIdx] : null;

  return (
    <section
      className="krina-gallery-section"
      aria-label="Krina at work — photo gallery"
      data-testid="krina-gallery"
    >
      <div
        className="krina-gallery-viewport"
        onMouseEnter={() => setIsPaused(true)}
        onMouseLeave={() => { if (lightboxIdx === null) setIsPaused(false); }}
        onFocus={() => setIsPaused(true)}
        onBlur={() => { if (lightboxIdx === null) setIsPaused(false); }}
        onTouchStart={pauseAndResume}
        onScroll={pauseAndResume}
      >
        <button
          type="button"
          className="krina-gallery-nav krina-gallery-nav-prev"
          onClick={() => step(-1)}
          aria-label="Scroll gallery left"
          data-testid="krina-gallery-prev"
        >
          <ChevronLeft size={18} />
        </button>

        <div
          className={`krina-gallery-track ${isPaused ? 'is-paused' : ''}`}
          ref={trackRef}
          data-testid="krina-gallery-track"
          style={{ '--marquee-duration': `${totalDurationSec * 2}s` }}
        >
          {[...galleryImages, ...galleryImages].map((img, idx) => {
            const originalIdx = idx % galleryImages.length;
            return (
              <button
                type="button"
                key={`gallery-${img.id}-${idx}`}
                className="krina-gallery-tile"
                onClick={() => openLightbox(originalIdx)}
                aria-hidden={idx >= galleryImages.length ? 'true' : undefined}
                aria-label={`Enlarge photo: ${img.caption || 'clinic photograph'}`}
                data-testid={`krina-gallery-tile-${originalIdx}`}
                tabIndex={idx >= galleryImages.length ? -1 : 0}
              >
                <img
                  src={img.thumb}
                  alt={idx >= galleryImages.length ? '' : (img.alt || img.caption)}
                  width="800"
                  height="800"
                  loading="lazy"
                  decoding="async"
                  className="krina-gallery-tile-img"
                />
                <span className="krina-gallery-tile-zoom" aria-hidden="true">
                  <ZoomIn size={20} />
                </span>
                {img.caption && (
                  <span className="krina-gallery-tile-caption">{img.caption}</span>
                )}
              </button>
            );
          })}
        </div>

        <button
          type="button"
          className="krina-gallery-nav krina-gallery-nav-next"
          onClick={() => step(1)}
          aria-label="Scroll gallery right"
          data-testid="krina-gallery-next"
        >
          <ChevronRight size={18} />
        </button>
      </div>

      {currentImage && (
        <div
          className="krina-lightbox"
          role="dialog"
          aria-modal="true"
          aria-label={currentImage.caption || 'Photograph enlarged'}
          onClick={closeLightbox}
          data-testid="krina-lightbox"
          onTouchStart={onTouchStart}
          onTouchEnd={onTouchEnd}
        >
          <button
            type="button"
            className="krina-lightbox-close"
            onClick={closeLightbox}
            aria-label="Close photo viewer"
            data-testid="krina-lightbox-close"
          >
            <X size={22} />
          </button>

          <button
            type="button"
            className="krina-lightbox-nav krina-lightbox-nav-prev"
            onClick={(e) => { e.stopPropagation(); goPrev(); }}
            aria-label="Previous photo"
            data-testid="krina-lightbox-prev"
          >
            <ChevronLeft size={28} />
          </button>

          <figure
            className="krina-lightbox-figure"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              key={currentImage.id}
              src={currentImage.full}
              alt={currentImage.alt || currentImage.caption}
              decoding="async"
              className="krina-lightbox-img"
              data-testid="krina-lightbox-img"
            />
            {currentImage.caption && (
              <figcaption className="krina-lightbox-caption">
                {currentImage.caption}
              </figcaption>
            )}
          </figure>

          <button
            type="button"
            className="krina-lightbox-nav krina-lightbox-nav-next"
            onClick={(e) => { e.stopPropagation(); goNext(); }}
            aria-label="Next photo"
            data-testid="krina-lightbox-next"
          >
            <ChevronRight size={28} />
          </button>

          <span
            className="krina-lightbox-counter"
            data-testid="krina-lightbox-counter"
          >
            {lightboxIdx + 1} / {galleryImages.length}
          </span>
        </div>
      )}
    </section>
  );
};

export default KrinaGallery;
